import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, X, MapPin, Globe, Home, ShoppingCart,
  ShoppingBag, User, ShoppingBasket, ChevronRight, ChevronDown,
} from "lucide-react";

import logo from "../../assets/app/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchAllCommercePartners } from "../../redux/commercePartnersSlice";
import {
  businessType,
  storeType,
  CategoryItem,
  renderCategoryIcon,
} from "../../CATEGORIES/PartnerCategories";

/* ═══════════════════════════════════════════════════════════
   TOKENS — PEEKABEBI: logo orange→red + cream/peach
   (matches CategoryDetails / ClientDashboard / SellerDashboard)
═══════════════════════════════════════════════════════════ */
const F = {
  pageBg:        "#FFF8F1",
  headerBg:      "#FF6A2A",
  cardBg:        "#FFFFFF",

  gradH:         "linear-gradient(90deg,#FF9A3D 0%,#FF3D57 100%)",
  gradAnim:      "linear-gradient(90deg,#FFB347 0%,#FF3D57 50%,#FFB347 100%)",
  grad:          "linear-gradient(135deg,#FF9A3D 0%,#FF3D57 100%)",

  orange:        "#FF7A1A",
  red:           "#FF3D57",
  green:         "#1FAA59",
  offwhite:      "#FFF3E6",
  ink:           "#2B1A12",
  muted:         "#9C8A7E",
  mutedDim:      "#B79C87",
  border:        "rgba(43,26,18,0.10)",
};

const GRAD_H = F.gradH;
const GRAD_A = F.gradAnim;

const ACCENTS = [F.orange, F.red, "#FF9A3D", "#E0501E", "#2D6CDF", "#3FA9F5", "#FFD700", "#9C27B0"];
const storeAccent = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return ACCENTS[h % ACCENTS.length];
};

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button { font-family: inherit; }

@keyframes gradSpin {
  0%   { background-position: 0%   50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0%   50%; }
}
@keyframes mc-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }

input::placeholder { color: rgba(255,255,255,.55); }

.s-card {
  transition: transform .2s cubic-bezier(.34,1.56,.64,1), box-shadow .2s;
  cursor: pointer;
  will-change: transform;
}
.s-card:hover  { transform: translateY(-6px); box-shadow: 0 16px 40px rgba(255,122,26,.14) !important; }
.s-card:active { transform: translateY(-2px) scale(.97); }

.cat-pill {
  transition: background .15s, border-color .15s, color .15s;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-item {
  display: flex; flex-direction: column; align-items: center;
  gap: 3px; font-size: 10px; font-weight: 700; cursor: pointer;
  color: ${F.mutedDim}; padding: 4px 10px; transition: color .2s;
}
.tab-item.active { color: ${F.orange}; }

/* ── page-wide watermark logo — fixed, behind everything ── */
.page-watermark{
  position:fixed;top:50%;left:50%;
  width:min(70vw,640px);height:min(70vw,640px);
  transform:translate(-50%,-50%);
  opacity:.035;pointer-events:none;z-index:0;
  object-fit:contain;
}
`;

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const Stores: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const authUser  = useSelector((s: RootState) => s.auth.user);
  const userId    = authUser?.userId;
  const partners  = useSelector((s: RootState) => s.commercePartners.partners);
  const cartItems = useSelector((s: RootState) => s.cart.items);
  const cartTotal = cartItems.reduce((s: number, i: any) => s + i.qty, 0);

  const [isMobile,        setIsMobile]       = useState(window.innerWidth <= 768);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchFocused,   setSearchFocused]   = useState(false);
  const [showGlobal,      setShowGlobal]      = useState(false);
  const [userCountry,     setUserCountry]     = useState<string | null>(null);
  const [userCity,        setUserCity]        = useState<string | null>(null);
  const [locationDenied,  setLocationDenied]  = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);

  /* selected business type (main category) */
  const [activeBizType, setActiveBizType]     = useState<CategoryItem | null>(null);
  /* selected store type (subcategory) — only relevant when activeBizType is "Stores" */
  const [activeStoreType, setActiveStoreType] = useState<CategoryItem | null>(null);
  /* storeType sheet open */
  const [storeTypeOpen, setStoreTypeOpen]     = useState(false);

  /* ── responsive ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── data ── */
  useEffect(() => { dispatch(fetchAllCommercePartners()); }, [dispatch]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) { setLocationDenied(true); setLocationLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if ((window as any).google?.maps) {
          new (window as any).google.maps.Geocoder().geocode(
            { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
            (res: any, st: string) => {
              if (st === "OK" && res?.[0]) {
                const g = (t: string) => res[0].address_components.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(g("locality") || g("administrative_area_level_2"));
                setUserCountry(g("country"));
              }
              setLocationLoading(false);
            }
          );
        } else setLocationLoading(false);
      },
      () => { setLocationDenied(true); setLocationLoading(false); }
    );
  }, []);

  /* ── derived lists ── */
  const published = useMemo(() => partners.filter((p: any) => p.isPublished && !p.deleted), [partners]);

  const localStores = useMemo(() => {
    if (!userCountry) return [];
    return published.filter((p: any) => p.address?.country?.toLowerCase() === userCountry.toLowerCase());
  }, [published, userCountry]);

  const baseList = useMemo(() => {
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      return published.filter((p: any) =>
        `${p.name ?? ""} ${p.address?.city ?? ""} ${p.address?.country ?? ""}`.toLowerCase().includes(q)
      );
    }
    return showGlobal || !localStores.length ? published : localStores;
  }, [searchQuery, showGlobal, published, localStores]);

  /* filter by selected business type / store type */
  const displayList = useMemo(() => {
    if (!activeBizType) return baseList;
    // if Stores (id=12) + a store sub-type selected
    if (activeBizType.id === 12 && activeStoreType) {
      return baseList.filter((p: any) =>
        p.categoryId === activeBizType.id && p.subcategoryId === activeStoreType.id
      );
    }
    return baseList.filter((p: any) => p.categoryId === activeBizType.id);
  }, [baseList, activeBizType, activeStoreType]);

  const isSearching = searchQuery.trim().length > 0;

  /* ── handlers ── */
  const handleBizTypeClick = (cat: CategoryItem) => {
    if (activeBizType?.id === cat.id) {
      // deselect
      setActiveBizType(null);
      setActiveStoreType(null);
      setStoreTypeOpen(false);
      return;
    }
    setActiveBizType(cat);
    setActiveStoreType(null);
    if (cat.id === 12) setStoreTypeOpen(true); // "Stores" → open sub-sheet
    else setStoreTypeOpen(false);
  };

  const handleStoreTypeClick = (st: CategoryItem) => {
    setActiveStoreType(prev => prev?.id === st.id ? null : st);
  };

  const goToStore    = (brandId: string) => navigate(`/StoreHome/${brandId}`);
  const goToCheckout = () => {
    if (!cartItems.length) return;
    navigate(`/productcheckout/${userId}`, {
      state: { userId, totalQty: cartTotal, totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0), items: cartItems },
    });
  };

  /* ══════════════════════════════════════════════════════════
     HEADER
  ══════════════════════════════════════════════════════════ */
  const Header = () => (
    <div style={{ background: F.headerBg, backgroundImage: F.grad, position:"sticky", top:0, zIndex:200, boxShadow:"0 4px 24px rgba(255,61,87,.25)" }}>
      <div style={{ background: GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 4s ease infinite", color:"#fff", textAlign:"center", fontSize:10, padding:"6px 0", letterSpacing:"0.18em", fontWeight:700 }}>
        ⚡ DISCOVER LOCAL &amp; GLOBAL STORES · FIND YOUR FAVOURITE BRANDS
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px" }}>
        {/* logo */}
        <div onClick={() => navigate(`/ProductsHome/${userId}`)} style={{ display:"flex", alignItems:"center", gap:7, cursor:"pointer", flexShrink:0 }}>
          <div style={{ background:"#fff", borderRadius:"50%", padding:2, display:"inline-flex" }}>
            <img src={logo} alt="PEEKABEBI" style={{ width:26, height:26, borderRadius:"50%", objectFit:"cover" }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
          </div>
          {!isMobile && <span style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:"'Baloo 2',sans-serif", letterSpacing:"0.02em" }}>PEEKABEBI</span>}
        </div>

        {/* search */}
        <div style={{ display:"flex", alignItems:"center", flex:1, background:"rgba(255,255,255,.18)", borderRadius:999, border:`1.5px solid ${searchFocused ? "#fff" : "rgba(255,255,255,.32)"}`, boxShadow: searchFocused ? "0 0 0 3px rgba(255,255,255,.18)" : "none", transition:"border-color .2s,box-shadow .2s", overflow:"hidden" }}>
          <Search size={14} color="#fff" style={{ marginLeft:12, flexShrink:0, opacity:0.85 }} />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
            placeholder="Search stores worldwide…"
            style={{ flex:1, border:"none", outline:"none", padding:"9px 10px", fontSize:13, color:"#fff", background:"transparent", fontFamily:"inherit" }} />
          {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background:"none", border:"none", cursor:"pointer", padding:"0 10px", color:"#fff" }}><X size={13} /></button>}
        </div>

        {/* local / global */}
        {!locationDenied && !locationLoading && (
          <button onClick={() => setShowGlobal(v => !v)}
            style={{ display:"flex", alignItems:"center", gap:5, background: showGlobal ? "rgba(255,255,255,.10)" : "rgba(255,255,255,.22)", border:"1px solid rgba(255,255,255,.28)", cursor:"pointer", color:"#fff", fontSize:12, fontWeight:700, padding:"6px 12px", borderRadius:999, flexShrink:0, whiteSpace:"nowrap" as const }}>
            {showGlobal ? <Globe size={12} /> : <MapPin size={12} />}
            <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Local"}</span>
          </button>
        )}

        {/* cart */}
        <button onClick={goToCheckout}
          style={{ display:"flex", alignItems:"center", background: cartTotal > 0 ? "rgba(255,255,255,.32)" : "rgba(255,255,255,.16)", border:"1px solid rgba(255,255,255,.28)", cursor:"pointer", color:"#fff", padding:"6px 12px", borderRadius:999, flexShrink:0, position:"relative" }}>
          <ShoppingCart size={16} />
          {cartTotal > 0 && <span style={{ position:"absolute", top:-4, right:-4, background:"#fff", color:F.red, fontSize:9, fontWeight:900, width:17, height:17, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
        </button>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     BUSINESS TYPE BAR  (main category pills)
  ══════════════════════════════════════════════════════════ */
  const BizTypeBar = () => (
    <div style={{ background: F.headerBg, backgroundImage: F.grad, borderBottom:"1px solid rgba(255,255,255,.18)", padding:"10px 14px" }}>
      <div style={{ display:"flex", gap:8, overflowX:"auto", scrollbarWidth:"none" as any, WebkitOverflowScrolling:"touch" as any }}>
        {businessType.map((cat) => {
          const isSel = activeBizType?.id === cat.id;
          const color = cat.color ?? F.orange;
          return (
            <button key={cat.id} className="cat-pill"
              onClick={() => handleBizTypeClick(cat)}
              style={{
                display:"flex", alignItems:"center", gap:6,
                padding: isMobile ? "6px 12px" : "7px 14px",
                borderRadius:999,
                background: isSel ? "#fff" : "rgba(255,255,255,.16)",
                border:`1.5px solid ${isSel ? "#fff" : "rgba(255,255,255,.28)"}`,
                color: isSel ? color : "#fff",
                fontSize:12, fontWeight:700,
              }}>
              {renderCategoryIcon(cat.icon, isSel ? color : "#fff", 13)}
              {cat.name}
              {cat.id === 12 && <ChevronDown size={11} style={{ opacity:0.85 }} />}
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     STORE TYPE SHEET  (subcategory sheet when Stores selected)
  ══════════════════════════════════════════════════════════ */
  const StoreTypeSheet = () => (
    <AnimatePresence>
      {storeTypeOpen && activeBizType?.id === 12 && (
        <motion.div
          initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }} exit={{ height:0, opacity:0 }}
          transition={{ duration:.22 }}
          style={{ overflow:"hidden", background:F.offwhite, borderBottom:`1px solid ${F.border}` }}>
          <div style={{ padding:"10px 14px" }}>
            <div style={{ display:"flex", gap:8, overflowX:"auto", scrollbarWidth:"none" as any, WebkitOverflowScrolling:"touch" as any }}>
              {storeType.map((st) => {
                const isSel = activeStoreType?.id === st.id;
                const color = st.color ?? F.orange;
                return (
                  <button key={st.id} className="cat-pill"
                    onClick={() => handleStoreTypeClick(st)}
                    style={{
                      display:"flex", alignItems:"center", gap:6,
                      padding:"5px 12px", borderRadius:999,
                      background: isSel ? color : "#fff",
                      border:`1.5px solid ${isSel ? color : F.border}`,
                      color: isSel ? "#fff" : F.ink,
                      fontSize:11, fontWeight:700,
                      boxShadow: isSel ? `0 3px 10px ${color}44` : "0 1px 4px rgba(43,26,18,.06)",
                    }}>
                    {renderCategoryIcon(st.icon, isSel ? "#fff" : color, 12)}
                    {st.name}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ══════════════════════════════════════════════════════════
     LOCATION BAR
  ══════════════════════════════════════════════════════════ */
  const LocationBar = () => {
    if (locationLoading) return (
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", background:"rgba(255,122,26,.05)", borderBottom:`1px solid ${F.border}`, position:"relative", zIndex:1 }}>
        <div style={{ width:7, height:7, borderRadius:"50%", background:F.orange, animation:"mc-pulse 1.2s infinite" }} />
        <span style={{ fontSize:11, color:F.muted, fontWeight:600 }}>Detecting your location…</span>
      </div>
    );
    if (locationDenied) return (
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", background:"rgba(255,61,87,.04)", borderBottom:"1px solid rgba(255,61,87,.12)", position:"relative", zIndex:1 }}>
        <MapPin size={12} color={F.red} />
        <span style={{ fontSize:11, color:F.red, fontWeight:600 }}>Location unavailable — showing all stores</span>
      </div>
    );
    if (isSearching) return (
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", background:"rgba(255,122,26,.05)", borderBottom:"1px solid rgba(255,122,26,.10)", position:"relative", zIndex:1 }}>
        <Globe size={12} color={F.orange} />
        <span style={{ fontSize:11, color:F.orange, fontWeight:600 }}>Searching worldwide for "{searchQuery}"</span>
      </div>
    );
    return (
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", background:"rgba(255,122,26,.05)", borderBottom:`1px solid ${F.border}`, position:"relative", zIndex:1 }}>
        {showGlobal ? <Globe size={12} color={F.orange} /> : <MapPin size={12} color={F.orange} />}
        <span style={{ fontSize:11, color:F.orange, fontWeight:600 }}>
          {showGlobal
            ? `${published.length} stores worldwide`
            : localStores.length
              ? `${localStores.length} store${localStores.length !== 1 ? "s" : ""} near ${userCity || userCountry}`
              : `No stores near you — showing all ${published.length}`}
        </span>
        {activeBizType && (
          <span style={{ marginLeft:4, fontSize:11, color:F.muted, fontWeight:500 }}>
            · {activeBizType.name}{activeStoreType ? ` › ${activeStoreType.name}` : ""}
          </span>
        )}
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     STORE CARD  — white, 3D lift
  ══════════════════════════════════════════════════════════ */
  const StoreCard = ({ partner }: { partner: any }) => {
    const accent  = storeAccent(partner.brandId);
    const city    = partner.address?.city    || null;
    const country = partner.address?.country || null;
    const isLocal = !!(userCountry && country?.toLowerCase() === userCountry.toLowerCase());

    return (
      <div className="s-card"
        onClick={() => goToStore(partner.brandId)}
        style={{
          background:"#fff", borderRadius:20, overflow:"hidden",
          boxShadow:`0 2px 0 ${accent}18, 0 6px 24px rgba(43,26,18,.08)`,
          border:`1.5px solid ${accent}28`,
          display:"flex", flexDirection:"column", position:"relative",
        }}>
        {/* top strip */}
        <div style={{ height:3, background:`linear-gradient(90deg,${accent},${accent}88)` }} />

        <div style={{ padding: isMobile ? "14px 12px 12px" : "18px 16px 14px", display:"flex", flexDirection:"column", gap:10, flex:1 }}>
          {/* logo + name */}
          <div style={{ display:"flex", alignItems:"center", gap:11 }}>
            <div style={{ position:"relative", flexShrink:0 }}>
              <div style={{ position:"absolute", inset:-2, borderRadius:"50%", background:`linear-gradient(135deg,${accent},${accent}55)`, zIndex:0 }} />
              <div style={{ position:"relative", zIndex:1, background:"#fff", borderRadius:"50%", padding:2 }}>
                <img src={partner.image || logo} alt={partner.name}
                  style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius:"50%", objectFit:"cover", display:"block" }}
                  onError={e => { (e.currentTarget as HTMLImageElement).src = logo; }} />
              </div>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:900, fontSize: isMobile ? 13 : 15, color:F.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" as const, lineHeight:1.2, fontFamily:"'Baloo 2',sans-serif" }}>
                {partner.name || "Unnamed Store"}
              </div>
              {(city || country) && (
                <div style={{ display:"flex", alignItems:"center", gap:3, marginTop:3, color:F.muted, fontSize:10, fontWeight:600 }}>
                  <MapPin size={9} color={F.muted} />
                  <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" as const }}>
                    {[city, country].filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* badges */}
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" as const }}>
            <span style={{ background:`${accent}12`, border:`1px solid ${accent}38`, borderRadius:999, padding:"2px 9px", fontSize:9, fontWeight:800, color:accent }}>✓ Verified</span>
            {isLocal && <span style={{ background:"rgba(31,170,89,.10)", border:"1px solid rgba(31,170,89,.30)", borderRadius:999, padding:"2px 9px", fontSize:9, fontWeight:800, color:F.green }}>📍 Near You</span>}
          </div>

          {/* CTA */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:"auto", paddingTop:8, borderTop:`1px solid ${accent}18` }}>
            <span style={{ fontSize:11, fontWeight:700, color:accent }}>Visit Store</span>
            <div style={{ width:26, height:26, borderRadius:"50%", background:`${accent}12`, border:`1.5px solid ${accent}38`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <ChevronRight size={13} color={accent} />
            </div>
          </div>
        </div>

        {/* gloss */}
        <div style={{ position:"absolute", inset:0, borderRadius:20, background:"linear-gradient(155deg,rgba(255,255,255,.16) 0%,transparent 55%)", pointerEvents:"none" }} />
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     SECTION LABEL
  ══════════════════════════════════════════════════════════ */
  const SectionLabel = ({ text, count }: { text: string; count: number }) => (
    <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:18 }}>
      <div style={{ width:4, height:22, borderRadius:2, background:GRAD_H }} />
      <span style={{ fontWeight:900, fontSize: isMobile ? 15 : 18, color:F.ink, letterSpacing:-.2, fontFamily:"'Baloo 2',sans-serif" }}>{text}</span>
      <span style={{ fontSize:11, color:F.muted, background:F.offwhite, border:`1px solid ${F.border}`, padding:"2px 9px", borderRadius:999 }}>{count}</span>
      {activeBizType && (
        <button onClick={() => { setActiveBizType(null); setActiveStoreType(null); setStoreTypeOpen(false); }}
          style={{ display:"flex", alignItems:"center", gap:4, marginLeft:4, background:"none", border:"none", cursor:"pointer", color:F.muted, fontSize:11, fontWeight:600 }}>
          <X size={11} /> Clear
        </button>
      )}
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     EMPTY STATE
  ══════════════════════════════════════════════════════════ */
  const EmptyState = () => (
    <div style={{ textAlign:"center", padding:"60px 24px", position:"relative", zIndex:1 }}>
      <div style={{ fontSize:52, marginBottom:14 }}>🏪</div>
      <h3 style={{ fontWeight:900, fontSize:18, color:F.ink, marginBottom:8, fontFamily:"'Baloo 2',sans-serif" }}>
        {isSearching ? `No stores match "${searchQuery}"` : activeBizType ? `No ${activeBizType.name} near you` : "No stores found"}
      </h3>
      <p style={{ color:F.muted, fontSize:13, marginBottom:22 }}>
        {isSearching ? "Try a different name, city, or country." : "Check back soon as new stores join."}
      </p>
      <button onClick={() => { setSearchQuery(""); setActiveBizType(null); setActiveStoreType(null); setStoreTypeOpen(false); }}
        style={{ padding:"10px 26px", borderRadius:999, border:"none", background:GRAD_H, color:"#fff", fontWeight:800, cursor:"pointer", fontSize:13 }}>
        View All Stores
      </button>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     STORE GRID
  ══════════════════════════════════════════════════════════ */
  const StoreGrid = ({ stores }: { stores: any[] }) => (
    <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(220px,1fr))", gap: isMobile ? 10 : 16, position:"relative", zIndex:1 }}>
      {stores.map((p: any, i: number) => (
        <motion.div key={p.brandId}
          initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
          transition={{ delay: i * 0.035, type:"spring", stiffness:280, damping:24 }}>
          <StoreCard partner={p} />
        </motion.div>
      ))}
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     BOTTOM TABS
  ══════════════════════════════════════════════════════════ */
  const BottomTabs = () => isMobile && userId ? (
    <div style={{ position:"fixed", bottom:0, left:0, right:0, height:64, background:"#fff", display:"flex", justifyContent:"space-around", alignItems:"center", boxShadow:"0 -4px 24px rgba(255,122,26,.12)", zIndex:100, borderTop:`1px solid ${F.border}` }}>
      {[
        { icon:<Home size={22} />,           label:"Home",   active:false, action:() => navigate(`/ProductsHome/${userId}`) },
        { icon:<ShoppingBasket size={22} />,  label:"Stores", active:true,  action:() => {} },
        { label:"Cart", active:false, icon:(
            <div style={{ position:"relative" }}>
              <ShoppingCart size={22} />
              {cartTotal > 0 && <span style={{ position:"absolute", top:-5, right:-5, background:F.red, color:"#fff", fontSize:8, fontWeight:900, width:16, height:16, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{cartTotal}</span>}
            </div>
          ), action:goToCheckout },
        { icon:<ShoppingBag size={22} />, label:"Orders",  active:false, action:() => navigate(`/ClientDashboard/${userId}`) },
        { icon:<User size={22} />,          label:"Profile", active:false, action:() => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale:0.88 } as any} onClick={t.action}
          className={`tab-item${t.active ? " active" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.active ? F.orange : F.mutedDim })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ══════════════════════════════════════════════════════════
     SECTION TITLE
  ══════════════════════════════════════════════════════════ */
  const sectionLabel = useMemo(() => {
    if (isSearching) return `Results for "${searchQuery}"`;
    if (activeBizType) return activeStoreType ? activeStoreType.name : activeBizType.name;
    if (showGlobal)   return "All Stores";
    if (localStores.length) return `Near ${userCity || userCountry || "You"}`;
    return "All Stores";
  }, [isSearching, activeBizType, activeStoreType, showGlobal, localStores, userCity, userCountry, searchQuery]);

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight:"100vh", background:F.pageBg, paddingBottom:90, fontFamily:"'Montserrat',sans-serif", position:"relative", overflow:"hidden" }}>
      <style>{GLOBAL_CSS}</style>

      {/* page-wide watermark logo — behind everything */}
      <img src={logo} alt="" className="page-watermark" />

      <Header />
      <BizTypeBar />
      <StoreTypeSheet />
      <LocationBar />

      <div style={{ padding: isMobile ? "18px 10px" : "24px 24px", position:"relative", zIndex:1 }}>
        {displayList.length === 0
          ? <EmptyState />
          : <>
              <SectionLabel text={sectionLabel} count={displayList.length} />
              <StoreGrid stores={displayList} />
            </>
        }
      </div>

      <BottomTabs />
    </div>
  );
};

export default Stores;